import type {
  AdminStatus,
  AvailableTransition,
  CreateTicketInput,
  ExecuteTransition,
  Ticket,
  TicketDetail,
  UpdateTicket,
} from '@servicetim/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { type ItemsResponse, portalPath, useApiContext } from './client';

const base = portalPath('/tickets');

/** `GET /v1/tickets/{idOrKey}`: ticket + `display` map + `watching`/`canEdit`/`canTransition`. */
export function useTicket(idOrKey: string | undefined) {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: keys.ticket(idOrKey ?? '').detail,
    queryFn: ({ signal }) => request<TicketDetail>('GET', `${base}/${idOrKey}`, { signal }),
    enabled: idOrKey !== undefined,
  });
}

/** `POST /v1/tickets` (201). */
export function useCreateTicket() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: (input: CreateTicketInput) => request<Ticket>('POST', base, { body: input }),
    onSuccess: () => invalidate(keys.search.all, keys.views.all),
  });
}

/**
 * `PATCH /v1/tickets/{id}` with `If-Match: <version>` (412 on mismatch). Status changes go
 * through transitions, never through this patch.
 */
export function useUpdateTicket() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: ({ id, patch, ifMatch }: { id: string; patch: UpdateTicket; ifMatch: number }) =>
      request<TicketDetail>('PATCH', `${base}/${id}`, { body: patch, ifMatch }),
    onSuccess: (ticket) => invalidateTicket(keys, invalidate, ticket),
  });
}

export function useDeleteTicket() {
  const { request, keys, invalidate, remove } = useApiContext();
  return useMutation({
    mutationFn: (id: string) => request<void>('DELETE', `${base}/${id}`),
    onSuccess: (_v, id) => {
      remove(keys.ticket(id).scope);
      return invalidate(keys.search.all, keys.views.all);
    },
  });
}

/** `POST /v1/tickets/{id}/ack` (first response marker). */
export function useAcknowledgeTicket() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: (id: string) => request<TicketDetail>('POST', `${base}/${id}/ack`, { body: {} }),
    onSuccess: (ticket) => invalidateTicket(keys, invalidate, ticket),
  });
}

/* ----------------------------- transitions ----------------------------- */

/** `GET /v1/tickets/{id}/transitions`: transitions whose conditions pass for the caller. */
export function useTicketTransitions(ticketId: string | undefined) {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: keys.ticket(ticketId ?? '').transitions,
    queryFn: ({ signal }) =>
      request<ItemsResponse<AvailableTransition>>('GET', `${base}/${ticketId}/transitions`, {
        signal,
      }),
    select: (r) => r.items,
    enabled: ticketId !== undefined,
  });
}

/** `POST /v1/tickets/{id}/transitions/{tid}` with transition-form `fields` and optional comment. */
export function useExecuteTransition() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: ({
      id,
      transitionId,
      ...input
    }: Partial<ExecuteTransition> & { id: string; transitionId: string }) =>
      request<TicketDetail>('POST', `${base}/${id}/transitions/${transitionId}`, {
        body: { fields: input.fields ?? {}, comment: input.comment },
      }),
    onSuccess: (ticket) => invalidateTicket(keys, invalidate, ticket),
  });
}

/** `POST /v1/tickets/{id}/status`: admin status move without rules. */
export function useSetTicketStatus() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: ({ id, ...input }: AdminStatus & { id: string }) =>
      request<TicketDetail>('POST', `${base}/${id}/status`, { body: input }),
    onSuccess: (ticket) => invalidateTicket(keys, invalidate, ticket),
  });
}

/** Refreshes the ticket under both its id and its key plus every list that may show it. */
export function invalidateTicket(
  keys: ReturnType<typeof useApiContext>['keys'],
  invalidate: ReturnType<typeof useApiContext>['invalidate'],
  ticket: Pick<Ticket, 'id' | 'key'>,
) {
  return invalidate(
    keys.ticket(ticket.id).scope,
    keys.ticket(ticket.key).scope,
    keys.search.all,
    keys.views.all,
  );
}
